import React, { memo, useEffect } from 'react';

import Slice from '../slices/slice';
import Text from '../slices/text';
import Image from '../slices/image';
import ImageText from '../slices/image-text';
import TextText from '../slices/text-text';
import Hero from '../slices/hero';
import ProductList from '../slices/product-list';
import Video from '../slices/video';
import VideoText from '../slices/video-text';

import colorMap from '../slices/shared/color-map';

import { trackContentViewed } from '../../tracking';

import { PrismicArticleData, PrismicSlice } from '../../../../types';

import * as S from './styles';

type OwnProps = {
  id: string;
  data: PrismicArticleData;
};

type Props = OwnProps;

export const Article = ({ id, data }: Props) => {
  useEffect(() => {
    trackContentViewed(id);
  }, []);

  const renderSlice = (slice: PrismicSlice, index: number) => {
    const key = `${slice.type}_${index}`;
    const colors = colorMap(
      // @ts-ignore
      // `backgroundColor` does not exist on every slice
      // ignore this because `colorMap` accepts null or undefined
      slice.primary.backgroundColor || slice.primary.textColor
    );

    switch (slice.primary.__typename) {
      case 'PrismicSliceHeroTitle':
        return <Hero key={key} {...slice.primary} textColor={colors.text} />;
      case 'PrismicSliceText':
        return (
          <Slice key={key} colors={colors}>
            <S.PageContent alignLeft>
              <Text data={slice.primary.text} />
            </S.PageContent>
          </Slice>
        );
      case 'PrismicSliceImage':
        return (
          <Slice key={key} colors={colors}>
            <S.PageContent alignLeft>
              <Image {...slice.primary.image} />
            </S.PageContent>
          </Slice>
        );
      case 'PrismicSliceImageText':
        return (
          <Slice key={key} colors={colors}>
            <S.PageContent alignLeft>
              <ImageText {...slice.primary} />
            </S.PageContent>
          </Slice>
        );
      case 'PrismicSliceTextImage':
        return (
          <Slice key={key} colors={colors}>
            <S.PageContent alignLeft>
              <ImageText {...slice.primary} reverse />
            </S.PageContent>
          </Slice>
        );
      case 'PrismicSliceTextText':
        return (
          <Slice key={key} colors={colors}>
            <S.PageContent alignLeft>
              <TextText {...slice.primary} />
            </S.PageContent>
          </Slice>
        );
      case 'PrismicSliceProductList':
        return slice.primary.products ? (
          <Slice key={key} colors={colors}>
            <S.PageContent alignLeft>
              <ProductList {...slice.primary} />
            </S.PageContent>
          </Slice>
        ) : null;
      case 'PrismicSliceVideo':
        return (
          <Slice key={key} colors={colors}>
            <S.PageContent alignLeft>
              <Video data={slice.primary.video} />
            </S.PageContent>
          </Slice>
        );
      case 'PrismicSliceVideoText':
        return (
          <Slice key={key} colors={colors}>
            <S.PageContent alignLeft>
              <VideoText data={slice.primary} />
            </S.PageContent>
          </Slice>
        );
      case 'PrismicSliceTextVideo':
        return (
          <Slice key={key} colors={colors}>
            <S.PageContent alignLeft>
              <VideoText reverse data={slice.primary} />
            </S.PageContent>
          </Slice>
        );
      default:
        return null;
    }
  };

  return <S.Article>{data.body.map(renderSlice)}</S.Article>;
};

export default memo(Article);
